/**
 * `Image.alt` is TRI-STATE, and next/image can only express two of the states.
 *
 * The portal contract (docs/portal-api/05-types.md, 2026-08-25) is explicit:
 *
 *   null  →  nobody set alt text. "omit the `alt` attribute entirely rather
 *            than rendering alt='', which tells a screen reader the image is
 *            decorative."
 *   ""    →  an author deliberately marked it decorative. Skip it.
 *   text  →  use it.
 *
 * We cannot honour the first instruction literally: `next/image` REQUIRES an
 * `alt` prop, so "omit the attribute" is not available without dropping to a
 * plain <img> and losing the optimizer. Every consumer here was instead doing
 * `alt={image?.alt ?? ""}`, which collapses null into "" — i.e. silently
 * reclassifies "nobody labelled this" as "this is decorative", hiding real
 * images from screen readers.
 *
 * So null falls back to a caller-supplied DESCRIPTION — in practice the post or
 * module title. That is not invented alt text: a hero image of an article is
 * about that article, so its headline is the honest short description, and it
 * is strictly better than the silence we were emitting. An author who genuinely
 * wants the image skipped still gets that by setting "" — the one state that
 * must survive untouched, and the reason this is a helper rather than a `??`.
 */
export function imageAlt(
  alt: string | null | undefined,
  /** Short description to use when nobody set alt text — usually the title. */
  fallback?: string | null,
): string {
  // Deliberately decorative. Never override an author's explicit choice.
  if (alt === "") return "";

  const authored = (alt ?? "").trim();
  if (authored) return authored;

  // Unlabelled: describe it rather than declaring it decorative.
  return (fallback ?? "").trim();
}
